import React from 'react';
import { space } from 'styled-system';
import styled, { createGlobalStyle, css } from 'styled-components';

import useTranslation from 'hooks/use-translation';

import ROUTES from 'src/routes';
import { BottomDrawer, Button, Link } from 'components/core';

type MenuBottomDrawerDarkProps = {
  onClose: () => void;
  onCtaClick?: () => void;
  open: boolean;
  onLoginClick: () => void;
  onSignUpClick: () => void;
  toggleAccountDrawer: () => void;
  isLoggedIn: boolean;
};

export function MenuBottomDrawerDark({
  onClose,
  onCtaClick,
  open,
  onLoginClick,
  onSignUpClick,
  isLoggedIn,
  toggleAccountDrawer,
}: MenuBottomDrawerDarkProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      <DarkDrawerGlobalStyle />
      <BottomDrawer open={open} onClose={onClose} heading={t('common.menu', 'Menu')}>
        <MenuItemContainer>
          <Link href='/'>
            <MenuItem mt={35} mb={35} onClick={onCtaClick} onKeyDown={onClose} role='button' tabIndex={0}>
              {t('common.shopNow', 'Shop now')}
            </MenuItem>
          </Link>
          {/* ROUTES.DISPENSARY_B2B is external to this app (same domain, different app) - plain
              <a>, not <Link>. */}
          <MenuItem href={ROUTES.DISPENSARY_B2B} mb={35}>
            {t('common.forBusinesses', 'For Businesses')}
          </MenuItem>
        </MenuItemContainer>

        <ButtonContainer>
          {isLoggedIn ? (
            <MyAccountButton onClick={toggleAccountDrawer}>{t('common.myAccount', 'My Account')}</MyAccountButton>
          ) : (
            <>
              <LoginButton onClick={onLoginClick}>{t('common.login', 'Log in')}</LoginButton>
              <SignupButton onClick={onSignUpClick}>{t('common.signup', 'Sign up')}</SignupButton>
            </>
          )}
        </ButtonContainer>
      </BottomDrawer>
    </>
  );
}

const DarkDrawerGlobalStyle = createGlobalStyle`
  .MuiDrawer-paperAnchorBottom {
    background-color: var(--rds-bg-card) !important;
    color: var(--rds-cream);
  }
  .MuiDrawer-paperAnchorBottom h3 {
    color: var(--rds-cream);
  }
`;

const MenuItem = styled.a`
  ${space}
  font-size: 26px;
  cursor: pointer;
  color: var(--rds-cream);
`;

const MenuItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 50px 0;
`;

const ButtonStyles = css`
  height: 50px;
  width: 50%;
`;

const LoginButton = styled(Button)`
  ${ButtonStyles}
  background: transparent;
  color: var(--rds-cream);
  border: 1px solid var(--rds-cream);
  margin-right: 8px;
  font-size: 16px;
  text-transform: none;
`;

const SignupButton = styled(Button)`
  ${ButtonStyles}
  background: var(--rds-gold);
  color: var(--rds-text-on-gold);
  border: none;
  font-size: 16px;
  font-weight: 600;
  text-transform: none;

  &:hover {
    background: var(--rds-gold-bright);
  }
`;

const MyAccountButton = styled(LoginButton)`
  width: 100%;
  margin-right: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
`;
